import { Form, Input } from 'antd';
import type { FormItemProps } from 'antd';
import { cloneElement, useState, type ReactElement, type ReactNode } from 'react';
import { FloatingLabelBox } from './FloatingLabelBox';

interface Props extends Omit<FormItemProps, 'label' | 'children'> {
  label: string;
  name: string;
  required?: boolean;
  children: ReactElement;
  /**
   * Optional node rendered absolute at the top-right of the field wrapper.
   * Useful for icons (e.g. sync) on TextArea where antd's `suffix` prop
   * isn't supported.
   */
  suffix?: ReactNode;
}

export function FloatingField({
  label,
  name,
  required,
  children,
  suffix,
  className,
  ...rest
}: Props) {
  const [focused, setFocused] = useState(false);
  const value = Form.useWatch(name);
  const hasValue = value !== undefined && value !== null && value !== '';

  // TextArea needs different vertical alignment — anchor label to top.
  const isTextArea = children.type === Input.TextArea;

  // Don't overwrite placeholder — children may need it (e.g. DatePicker shows
  // 'DD/MM/YYYY' below the floated label). Only inject focus handlers.
  const childProps = children.props as {
    onFocus?: (e: unknown) => void;
    onBlur?: (e: unknown) => void;
  };
  const childWithEvents = cloneElement(children, {
    onFocus: (e: unknown) => {
      setFocused(true);
      childProps.onFocus?.(e);
    },
    onBlur: (e: unknown) => {
      setFocused(false);
      childProps.onBlur?.(e);
    },
  });

  return (
    <FloatingLabelBox
      label={label}
      required={required}
      isFloating={focused || hasValue}
      suffix={suffix}
      className={[isTextArea ? 'has-textarea' : '', className ?? ''].filter(Boolean).join(' ')}
    >
      <Form.Item name={name} {...rest}>
        {childWithEvents}
      </Form.Item>
    </FloatingLabelBox>
  );
}
